import StoreWorkspace from './pages/Store/StoreWorkspace';
import CreationWorkspace from './components/CreatePost/CreationWorkspace';
import ProfilePreviewProvider from './contexts/ProfilePreviewProvider';
// App.js
import React from 'react';
import RealtimeConnection from './components/RealtimeConnection';
import TasbeehSync from './components/Tasbeeh/TasbeehSync';
import OfflineTasbeehSync from './components/Maintenance/OfflineTasbeehSync';
import AdhkarOfflineSync from './components/Maintenance/AdhkarOfflineSync';
import { MaintenanceProvider } from './contexts/MaintenanceContext';
import MaintenanceGate from './components/Maintenance/MaintenanceGate';
import { BrowserRouter as Router, Route, Outlet, useLocation, useNavigate, matchPath, Navigate } from 'react-router-dom';
import AnimatedRoutes from './components/Motion/AnimatedRoutes';
import DesktopRouteLayout from './components/DesktopLayout/DesktopRouteLayout';
import { UserProvider, UserContext } from './components/User/UserContext';
import ProfileSetupGate from './components/User/ProfileSetupGate';
import BottomNavbar from './components/BottomNavbar/BottomNavbar';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import LocalizationPreferenceSync from './components/LocalizationPreferenceSync';
import { SnackbarProvider } from 'notistack';
import NotificationListener from './components/NotificationListener/NotificationListener';
import { ModalProvider, ModalContext } from './components/ModalContext';
import { QueryProvider } from './providers/QueryProvider';
import { ToastProvider, useToast } from './components/ToastMessage/Toast';
import './App.css';
import PushNotificationPrompt from './components/PushNotificationPrompt/PushNotificationPrompt';
// ─── [جديد] نظام تتبع حالة المستخدم ─────────────────────────────────────
import { BAN_TYPES, BAN_TYPE_LABELS, getActiveBanRecord } from './utils/moderationBan';
import TermsOfService from './pages/TermsOfService/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy/PrivacyPolicy';
import { getTopModal, isBackNavigation } from './utils/modalHistory';
import { useAuth } from './contexts/AuthContext';
import { useAccess } from './contexts/AccessContext';
import VideoPublishingStatus from './components/VideoPublishingStatus/VideoPublishingStatus';
import ChatVideoStatus from './components/ChatVideoStatus/ChatVideoStatus';
import PushNotificationClickHandler from './components/PushNotificationClickHandler/PushNotificationClickHandler';

const MessagesPage = React.lazy(() => import('./pages/MessagesPage/MessagesPage'));
const MaintenanceManager = React.lazy(() => import('./pages/Maintenance/MaintenanceManager'));
const Events = React.lazy(() => import('./pages/Events/Events'));
const ApiChatRoomPage = React.lazy(() => import('./pages/ChatRoomPage/ApiChatRoomPage'));
const CreateGroupPage = React.lazy(() => import('./pages/CreateGroupPage/CreateGroupPage'));
const DiscoverGroupsPage = React.lazy(() => import('./pages/DiscoverGroupsPage/DiscoverGroupsPage'));
const LoginPage = React.lazy(() => import('./pages/Login/LoginPage'));
const VerifyEmailPage = React.lazy(() => import('./pages/BackendAuth/VerifyEmailPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/BackendAuth/ResetPasswordPage'));
const Register = React.lazy(() => import('./pages/Register/Register'));
const FriendsPage = React.lazy(() => import('./pages/FriendsPage/FriendsPage'));
const ContactsPage = React.lazy(() => import('./pages/ContactsPage/ContactsPage'));
const BlockedUsersPage = React.lazy(() => import('./pages/BlockedUsersPage/BlockedUsersPage'));
const BackendProfile = React.lazy(() => import('./pages/BackendProfile/BackendProfile'));
const CreatePost = React.lazy(() => import('./components/CreatePost/CreatePostEntry'));
const BackendHomePosts = React.lazy(() => import('./components/BackendHomePosts/BackendHomePosts'));
const BackendCommentThreadPage = React.lazy(() => import('./pages/BackendCommentThread/BackendCommentThreadPage'));
const BackendPostPage = React.lazy(() => import('./pages/BackendPost/BackendPostPage'));
const SinglePollPage = React.lazy(() => import('./pages/SinglePollPage/SinglePollPage'));
const DeletedPostsPage = React.lazy(() => import('./pages/DeletedPostsPage/DeletedPostsPage'));
const StaffPage = React.lazy(() => import('./pages/StaffPage/StaffPage'));
const ModerationLogsPage = React.lazy(() => import('./pages/ModerationLogsPage/ModerationLogsPage'));
const DeletedAccountsPage = React.lazy(() => import('./pages/DeletedAccountsPage/DeletedAccountsPage'));
const AdminNotificationsPage = React.lazy(() => import('./pages/AdminNotificationsPage/AdminNotificationsPage'));
const WalletCurrenciesPage = React.lazy(() => import('./pages/WalletCurrenciesPage/WalletCurrenciesPage'));
const WalletPaymentPage = React.lazy(() => import('./pages/WalletPaymentPage/WalletPaymentPage'));
const ProfileBadgesPage = React.lazy(() => import('./pages/ProfileBadgesPage/ProfileBadgesPage'));
const TeamPage = React.lazy(() => import('./pages/TeamPage/ApiTeamPage'));
const HashtagPage = React.lazy(() => import('./pages/HashtagPage/HashtagPage'));
const TeamPostsPage = React.lazy(() => import('./pages/TeamPostsPage/TeamPostsPage'));
const StickerStorePage = React.lazy(() => import('./pages/CollectionStores/StickerStorePage'));
const ReactionStorePage = React.lazy(() => import('./pages/CollectionStores/ReactionStorePage'));
const ProfileThemeStorePage = React.lazy(() => import('./pages/CollectionStores/ProfileThemeStorePage'));
const FramesStorePage = React.lazy(() => import('./pages/FramesStorePage/FramesStorePage'));
const CreatePostListPage = React.lazy(() => import('./pages/CreatePostListPage/CreatePostListPage'));
const PostListPage = React.lazy(() => import('./pages/PostListPage/PostListPage'));
const EditProfilePage = React.lazy(() => import('./pages/EditProfilePage/EditProfilePage'));
const CreateProfilePage = React.lazy(() => import('./pages/CreateProfilePage/CreateProfilePage'));
const ProfileMenu = React.lazy(() => import('./pages/ProfileMenu/ProfileMenu'));
const Settings = React.lazy(() => import('./pages/Settings/Settings'));
const Tasbeeh = React.lazy(() => import('./pages/Tasbeeh/Tasbeeh'));
const Adhkar = React.lazy(() => import('./pages/Tasbeeh/Adhkar'));
const Counter = React.lazy(() => import('./pages/Tasbeeh/Counter'));
const Leaderboard = React.lazy(() => import('./pages/Tasbeeh/Leaderboard'));
const PrayerTracker = React.lazy(() => import('./pages/Tasbeeh/PrayerTracker'));
const ProfileMediaReview = React.lazy(() => import('./pages/ProfileMediaReview/ProfileMediaReview'));
const CompetitionManager = React.lazy(() => import('./pages/Tasbeeh/CompetitionManager'));
const ShieldManager = React.lazy(() => import('./pages/Tasbeeh/ShieldManager'));
const AccountVerification = React.lazy(() => import('./pages/AccountVerification/AccountVerification'));
const NotificationsPage = React.lazy(() => import('./pages/NotificationsPage/NotificationsPage'));
const SavedPosts = React.lazy(() => import('./pages/SavedPosts/SavedPosts'));
const DraftsPage = React.lazy(() => import('./pages/DraftsPage/DraftsPage'));
const MyReportsPage = React.lazy(() => import('./pages/MyReportsPage/MyReportsPage'));
const SuggestionsBoxPage = React.lazy(() => import('./pages/SuggestionsBoxPage/SuggestionsBoxPage'));
const PoliceReportsPage = React.lazy(() => import('./pages/PoliceReportsPage/PoliceReportsPage'));
const LiteratureReportsPage = React.lazy(() => import('./pages/LiteratureReportsPage/LiteratureReportsPage'));
const LawReportsPage = React.lazy(() => import('./pages/LawReportsPage/LawReportsPage'));
const HealthReportsPage = React.lazy(() => import('./pages/HealthReportsPage/HealthReportsPage'));
const ReportsPage = React.lazy(() => import('./pages/ReportsPage/ReportsPage'));
const ManageHashtagsPage = React.lazy(() => import('./pages/ManageHashtagsPage/ManageHashtagsPage'));
const Store = React.lazy(() => import('./pages/Store/BackendStore'));
const InventoryPage = React.lazy(() => import('./pages/Store/InventoryPage'));
const QuranPage = React.lazy(() => import('./pages/QuranPage/QuranPage'));
const MyQuranListPage = React.lazy(() => import('./pages/MyQuranListPage/MyQuranListPage'));
const EmergencyLogin = React.lazy(() => import('./pages/EmergencyLogin/EmergencyLogin'));
const CharactersPage = React.lazy(() => import('./pages/CharactersPage/CharactersPage'));
const CharacterDetailsPage = React.lazy(() => import('./pages/CharactersPage/CharacterDetailsPage'));
const CreatePoll = React.lazy(() => import('./components/CreatePost/CreatePoll'));
const SingleVideoPost = React.lazy(() => import('./pages/SingleVideoPost/SingleVideoPost'));
const CreatePlan = React.lazy(() => import('./pages/CreatePlanPage/CreatePlan'));
const DailyQuiz = React.lazy(() => import('./pages/DailyQuiz/DailyQuiz'));
const DailyMissions = React.lazy(() => import('./pages/DailyMissions/DailyMissions'));
const CreateDailyQuizPage = React.lazy(() => import('./pages/CreateDailyQuizPage/CreateDailyQuizPage'));
const CreateQuizPage = React.lazy(() => import('./pages/CreateQuizPage/CreateQuizPage'));
const ChildSafety = React.lazy(() => import('./pages/ChildSafety/ChildSafety'));
const BubbleStorePage = React.lazy(() => import('./pages/BubbleStorePage/BubbleStorePage'));
const IslamicHub = React.lazy(() => import('./pages/IslamicHubPage/IslamicHub'));
const CreateEvent = React.lazy(() => import('./pages/CreateEvent/CreateEvent'));
const UniversalQuizEngine = React.lazy(() => import('./pages/TalentQuiz/UniversalQuizEngine'));
const TalentsBadges = React.lazy(() => import('./pages/TalentBadges/TalentsBadges'));
const TalentLevels = React.lazy(() => import('./pages/TalentBadges/TalentLevels'));
const AdminTalentManager = React.lazy(() => import('./pages/TalentBadges/AdminTalentManager'));
const GamesPage = React.lazy(() => import('./pages/GamesPage/GamesPage'));
const PacksBrowser = React.lazy(() => import('./pages/Tasbeeh/PacksBrowser'));
const PackEditor = React.lazy(() => import('./pages/Tasbeeh/PackEditor'));
const SarahaPage = React.lazy(() => import('./pages/SarahaPage/SarahaPage'));
const SarahaSendPage = React.lazy(() => import('./pages/SarahaSendPage/SarahaSendPage'));


const RouteFallback = () => (
  <div
    style={{
      minHeight: '40vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#666',
      fontSize: '16px',
      fontWeight: 600,
    }}
  >
    جاري تحميل الصفحة...
  </div>
);

// Create a wrapper component for BottomNavbar
const BottomNavWrapper = () => {
  const location = useLocation();
  const { loading } = React.useContext(UserContext);
  const { currentModal } = React.useContext(ModalContext);
  const [hasActiveModal, setHasActiveModal] = React.useState(false);

  React.useEffect(() => {
    const overlaySelector = [
      '.post-modal-overlay',
      '.comment-dialog-overlay',
      '.profile-modal-overlay',
      '.reaction-dialog-overlay',
      '.image-viewer-overlay',
      '.saraha-overlay',
      '.send-dialog-overlay',
      '.icon-dialog-overlay',
      '.single-video-overlay',
      '.modal-overlay'
    ].join(',');

    const computeHasModal = () => {
      const hasModal = !!document.querySelector(overlaySelector);
      setHasActiveModal(hasModal);
    };

    computeHasModal();

    let frame = null;
    const observer = new MutationObserver(() => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        computeHasModal();
      });
    });
    // Animation libraries update inline styles every frame. Overlay visibility
    // depends on insertion/removal and classes, not those style mutations.
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });

    return () => {
      observer.disconnect();
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, []);

  const hiddenPatterns = [
    '/create',
    '/post/:post_id/comments',
    '/post/:post_id/comment/:comment_id/single',
    '/post/:post_id/comment/:comment_id/replies/:reply_id',
    '/post/:post_id/comment/:comment_id/replies/:reply_id/single',
    '/post/:post_id/comment/:comment_id/reply/:reply_id/single',
    '/post/:post_id/comments/:comment_id',
    '/post/:post_id/comments/:comment_id/single',
    '/post/:post_id/comments/:comment_id/replies/:reply_id',
    '/post/:post_id/comments/:comment_id/replies/:reply_id/single',
    '/post/:post_id/comment/:comment_id',
    '/post/:post_id/comment/:comment_id/reply/:reply_id',
    '/post/:postId',
    '/blog/:postId',
    '/quiz/:postId',
    '/newspaper/:postId',
    '/poll/:postId',
    '/video/:postId',
    '/character/:category/:id',
    '/profile/:profile_id',
    '/list/:profile_id/:id',
    '/administrators',
    '/hashtag/:hashtag',
    '/team/:teamId/posts',
    '/frames-store', '/sticker-store', '/reaction-store', '/profile-theme-store',
    '/bubble-store', '/bubble-store/manage',
    '/store',
    '/inventory',
    '/quran',
    '/my-quran-list',
    '/characters',
    '/characters/:category/:character_id',
    '/edit-profile',
    '/tasbeeh',
    '/notifications',
    '/saved-posts',
    '/suggestion-report',
    '/suggestions-box',
    '/police-reports',
    '/literature-reports',
    '/law-reports',
    '/health-reports',
    '/reports',
    '/tasbeeh/counter',
    '/login',
    '/emergency-login',
    '/create-profile',
    '/manage-hashtags',
    '/create-poll',
    '/create-video',
    '/create-quiz',
    '/settings',
    '/daily_quiz',
    '/adhkar',
    '/create-post-list',
    '/friends',
    '/messages',
    '/messages/create-group',
    '/messages/chat/:id',
    '/messages/group/:groupid',
    '/contacts',
    '/blocked-users',
    '/messages/discover',
    '/prayer-tracker',
    '/create-plan',
    '/my-reports',
    '/admin/talents',
    '/talents',
    '/TalentsBadges/:categoryKey',
    '/talentQuiz/:category',
    '/events',
    '/create-event',
    '/edit-event/:eventId',
    '/adhkar-packs',
    '/pack-editor/:packId',
    '/saraha',
    '/team',
    '/drafts',
    '/games',
    '/moderation-logs',
    '/deleted_posts',
    '/admin/deleted-accounts',
    '/admin/custom-notifications',
    '/admin/wallet-currencies',
    '/wallet',
    '/wallet/pay/:requestId',
    '/admin/verification', '/admin/profile-badges', '/admin/maintenance', '/offline', '/admin/profile-media', '/admin/tasbeeh-shields', '/admin/tasbeeh-competitions'
  ];

  const shouldHide = hiddenPatterns.some(pattern => matchPath({ path: pattern, end: true }, location.pathname)) || location.pathname.startsWith('/tasbeeh') || loading || hasActiveModal || currentModal !== null;

  if (shouldHide) {
    return null;
  }
  return <BottomNavbar />;
};

// Keep HomePosts mounted and hide/show it by route to preserve scroll, images, and in-memory state
const PersistentHomePostsLayer = () => {
  const location = useLocation();
  const { userData, loading } = React.useContext(UserContext);
  const [hasMountedHome, setHasMountedHome] = React.useState(false);

  const isHomeRoute =
    matchPath({ path: '/', end: true }, location.pathname) ||
    matchPath({ path: '/post', end: true }, location.pathname);

  React.useEffect(() => {
    if (isHomeRoute) {
      setHasMountedHome(true);
    }
  }, [isHomeRoute]);

  if (loading || !userData || !userData.name || !userData.tag) {
    return null;
  }

  // Don't mount HomePosts until it is visited once.
  if (!hasMountedHome && !isHomeRoute) {
    return null;
  }

  return (
    <div style={{ display: isHomeRoute ? 'block' : 'none' }}>
      <BackendHomePosts />
    </div>
  );
};

// The layout lives inside Routes' displayed-location context so snapshots also
// include the persistent feed and the correct navigation bar.
const AnimatedPageLayout = () => (
  <ProfileSetupGate>
    <React.Suspense fallback={<RouteFallback />}>
      <PersistentHomePostsLayer />
      <DesktopRouteLayout><Outlet /></DesktopRouteLayout>
    </React.Suspense>
    <BottomNavWrapper />
  </ProfileSetupGate>
);

// Protected Route Component
const ProtectedRoute = ({ allowedRoles, children }) => {
  const { userData } = React.useContext(UserContext);

  if (!userData || !allowedRoles.includes(userData.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const PermissionRoute = ({ anyOf, children }) => {
  const { loading, hasAnyPermission } = useAccess();
  if (loading) return <RouteFallback />;
  return hasAnyPermission(...anyOf) ? children : <Navigate to="/" replace />;
};

const BanBlockedScreen = ({ title, message, onBack }) => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#050505',
    color: '#fff',
    textAlign: 'center',
    padding: '24px',
  }}>
    <div>
      <div style={{ fontSize: 'clamp(28px, 8vw, 52px)', fontWeight: 800, marginBottom: 12 }}>أنت محظور</div>
      {title && <div style={{ fontSize: 18, marginBottom: 8, color: '#ffb3b3' }}>{title}</div>}
      {message && <div style={{ fontSize: 15, color: '#d0d0d0', lineHeight: 1.7, marginBottom: onBack ? 20 : 0 }}>{message}</div>}
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          style={{
            marginTop: 10,
            padding: '12px 20px',
            borderRadius: 999,
            border: 'none',
            background: '#2563eb',
            color: '#fff',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          العودة للخلف
        </button>
      )}
    </div>
  </div>
);

const BanRouteGuard = ({ banType, children }) => {
  const { userData, loading } = React.useContext(UserContext);
  const navigate = useNavigate();

  if (loading) {
    return null;
  }

  const activeBan = getActiveBanRecord(userData, banType);
  if (activeBan) {
    return (
      <BanBlockedScreen
        title={BAN_TYPE_LABELS[banType] || 'حظر نشط'}
        message={activeBan.reason || 'هذا القسم غير متاح بسبب الحظر الحالي.'}
        onBack={() => {
          if (window.history.length > 1) {
            navigate(-1);
          } else {
            navigate('/posts', { replace: true });
          }
        }}
      />
    );
  }

  return children;
};

const FullBanGate = ({ children }) => {
  const { userData, loading } = React.useContext(UserContext);
  const fullBan = getActiveBanRecord(userData, BAN_TYPES.FULL);

  if (loading) {
    return null;
  }

  if (fullBan) {
    return <BanBlockedScreen />;
  }

  return children;
};

// Require Profile Component
const RequireProfile = ({ children }) => {
  const { userData, loading, profileStatus, retryProfile } = React.useContext(UserContext);

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'white' }}>
        <img src="/assets/icons/icon-192x192.png" alt="App Icon" style={{ width: '80px', height: '80px', marginBottom: '20px' }} />
        <div>جاري التحميل...</div>
      </div>
    );
  }

  if (profileStatus === 'error') {
    return (
      <div dir="rtl" style={{ display: 'flex', flexDirection: 'column', gap: 14, justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: 24, textAlign: 'center', background: 'white' }}>
        <img src="/assets/icons/icon-192x192.png" alt="App Icon" style={{ width: '80px', height: '80px' }} />
        <div style={{ fontSize: 18, fontWeight: 700 }}>تعذّر التحقق من الملف الشخصي</div>
        <div style={{ color: '#666', maxWidth: 420 }}>لم نتمكن من الاتصال بالخادم. بياناتك آمنة، ولن يتم نقلك إلى إنشاء ملف شخصي جديد.</div>
        <button type="button" onClick={retryProfile} style={{ border: 0, borderRadius: 10, padding: '11px 24px', background: '#2563eb', color: 'white', fontWeight: 700, cursor: 'pointer' }}>
          إعادة المحاولة
        </button>
      </div>
    );
  }

  if (profileStatus === 'signed-out' || (!userData && profileStatus !== 'missing')) {
    return <Navigate to="/login" replace />;
  }

  // Only an authoritative, successful database read may open profile creation.
  if (profileStatus === 'missing') {
    return <Navigate to="/create-profile" replace />;
  }

  return children;
};

const LegacyCreateVideoRedirect = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  searchParams.set('type', 'video');

  return <Navigate to={`/create?${searchParams.toString()}`} replace />;
};

const PWA_EXIT_GUARD_STATE = '__amposterPwaExitGuard';
const PWA_EXIT_WINDOW_MS = 2500;

const PwaExitGuard = () => {
  const location = useLocation();
  const showToast = useToast();
  const { currentModal } = React.useContext(ModalContext);

  React.useEffect(() => {
    const isStandalone = window.matchMedia?.('(display-mode: standalone)').matches
      || window.navigator.standalone === true
      || document.referrer.startsWith('android-app://');
    const isHomeRoute = location.pathname === '/' || location.pathname === '/post';

    if (!isStandalone || !isHomeRoute) return undefined;

    let rearmTimer = null;

    const pushExitGuard = () => {
      if (window.history.state?.[PWA_EXIT_GUARD_STATE]) return;
      const currentState = window.history.state || {};
      const nextState = {
        ...currentState,
        [PWA_EXIT_GUARD_STATE]: true,
      };
      if (typeof currentState.idx === 'number') nextState.idx = currentState.idx + 1;
      window.history.pushState(nextState, '', window.location.href);
    };

    pushExitGuard();

    const handlePopState = (event) => {
      // Returning from a modal should reveal the Home feed, never start the
      // app-exit gesture. Registered and animated overlays cover both the
      // browser-Back and explicit close-button paths.
      const hasVisibleOverlay = Boolean(document.querySelector([
        '.post-modal-overlay',
        '.comment-dialog-overlay',
        '.profile-modal-overlay',
        '.reaction-dialog-overlay',
        '.image-viewer-overlay',
        '.single-video-overlay',
        '.modal-overlay',
      ].join(',')));

      if (event.state?.[PWA_EXIT_GUARD_STATE]
        || isBackNavigation()
        || getTopModal()
        || currentModal !== null
        || hasVisibleOverlay) {
        return;
      }

      if (window.location.pathname !== '/' && window.location.pathname !== '/post') return;

      showToast('اضغط على زر الرجوع مرة أخرى للخروج من التطبيق', {
        duration: PWA_EXIT_WINDOW_MS,
      });

      // Stay on the underlying Home entry for a short window. A second Back
      // exits the standalone PWA naturally. If it is not pressed, add the
      // guard again so a later Back requires confirmation once more.
      window.clearTimeout(rearmTimer);
      rearmTimer = window.setTimeout(pushExitGuard, PWA_EXIT_WINDOW_MS);
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.clearTimeout(rearmTimer);
    };
  }, [currentModal, location.pathname, showToast]);

  return null;
};

const BACKEND_READY_ROUTES = [
  '/', '/post', '/post/:postId', '/blog/:postId', '/quiz/:postId', '/newspaper/:postId', '/video/:postId', '/login', '/wallet', '/wallet/pay/:requestId', '/register', '/verify-email', '/reset-password',
  '/saraha', '/saraha/:userhandle',
  '/team', '/team/:teamId/posts',
  '/create-profile', '/edit-profile', '/profile/:user_id', '/create', '/create-poll',
  '/create-quiz', '/create-video', '/poll/:postId', '/messages',
  '/messages/chat/:id', '/messages/group/:id', '/notifications', '/saved-posts',
  '/administrators', '/moderation-logs', '/reports', '/my-reports', '/suggestions-box', '/deleted_posts', '/admin/deleted-accounts', '/admin/custom-notifications', '/admin/verification', '/admin/profile-badges', '/admin/maintenance', '/offline', '/admin/profile-media', '/admin/tasbeeh-shields', '/admin/tasbeeh-competitions',
  '/messages/create-group', '/messages/discover', '/contacts', '/blocked-users', '/friends',
  '/post/:post_id/comments', '/post/:post_id/comment/:comment_id',
  '/post/:post_id/comment/:comment_id/replies/:reply_id',
  '/post/:post_id/comment/:comment_id/reply/:reply_id/single',
  '/post/:post_id/comment/:comment_id/single', '/post/:post_id/comments/:comment_id',
  '/post/:post_id/comments/:comment_id/single',
  '/post/:post_id/comments/:comment_id/replies/:reply_id',
  '/post/:post_id/comments/:comment_id/replies/:reply_id/single',
  '/drafts', '/store', '/inventory', '/frames-store', '/sticker-store', '/reaction-store', '/profile-theme-store', '/bubble-store', '/bubble-store/manage', '/settings', '/more', '/terms', '/privacy', '/child-safety',
  '/prayer-tracker', '/adhkar', '/adhkar-packs', '/pack-editor/:packId',
  '/Tasbeeh', '/Tasbeeh/Counter', '/Tasbeeh/Leaderboard',
  '/create-plan',
  '/talents-badges',
  '/TalentsBadges/:categoryKey',
  '/quran', '/my-quran-list',
  '/islamic',
  '/hashtag/:hashtag', '/characters', '/character/:category/:id',
  '/characters/:category/:id', '/create-post-list', '/list/:userId/:listId',
  '/events', '/create-event', '/edit-event/:eventId',
  '/daily_quiz', '/create-daily-quiz', '/daily-missions',
  '/admin/talents', '/talents', '/games',
  '/admin/wallet-currencies',
];

const ApiFeatureGuard = ({ children }) => {
  const location = useLocation();
  const ready = BACKEND_READY_ROUTES.some((path) => matchPath({ path, end: true }, location.pathname));
  if (ready) return children;
  return (
    <main style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 20, background: '#f4f7f9', textAlign: 'center' }}>
      <section style={{ width: 'min(100%, 560px)', padding: 24, borderRadius: 16, background: '#fff' }}>
        <h1>Feature temporarily unavailable</h1>
        <p>This screen is still being connected to the Amposter API. It is disabled until its backend contract is complete so it cannot read or write legacy Firebase application data.</p>
        <a href="/" style={{ display: 'inline-block', padding: '10px 18px', borderRadius: 10, background: '#1687c4', color: '#fff', textDecoration: 'none' }}>Back to the feed</a>
      </section>
    </main>
  );
};

// Background services below call authenticated endpoints. Keep them completely
// unmounted while the visitor is logged out so public routes such as /login do
// not poll profiles, notifications, presence, or push-device registration.
const AuthenticatedAppServices = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading || !isAuthenticated) return null;

  return (
    <>
      <NotificationListener />
      <PushNotificationClickHandler />

      <PushNotificationPrompt />
      <VideoPublishingStatus />
      <ChatVideoStatus />
    </>
  );
};

function App() {
  const { currentUserId: activeAccountId, offlineAccountId, loading: authLoading, refreshSession, logout } = useAuth();
  React.useEffect(() => {
    const oldPrefixes = ['amposter_role_', 'amposter_winner_'];
    const keysToRemove = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (oldPrefixes.some((prefix) => key.startsWith(prefix)) && key !== 'amposter_role' && key !== 'amposter_winner') {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }, []);

  // Fetch the Home chunk alongside authentication on a cold Home visit.
  // Other primary tabs are warmed by BottomNavbar; editors and event dialogs
  // load on demand so they do not compete with the first feed paint.
  React.useEffect(() => {
    const loadHome = () => { void import('./components/BackendHomePosts/BackendHomePosts').catch(() => {}); };
    if (window.location.pathname === '/' || window.location.pathname === '/post') {
      loadHome();
      return undefined;
    }
    if (navigator.connection?.saveData) return undefined;
    if (window.requestIdleCallback) {
      const handle = window.requestIdleCallback(loadHome);
      return () => window.cancelIdleCallback(handle);
    }
    const timer = window.setTimeout(loadHome, 1200);
    return () => window.clearTimeout(timer);
  }, []);

  // React.useEffect(() => {
  //   if (typeof window === 'undefined') return;

  //   const isMobile = /Mobi|Android|iPhone|iPad|iPod/.test(navigator.userAgent) || window.innerWidth <= 900;
  //   if (!isMobile) return;

  //   const requestFullscreen = async () => {
  //     const elem = document.documentElement;
  //     if (elem.fullscreenElement || elem.webkitFullscreenElement || elem.msFullscreenElement) return;
  //     try {
  //       if (elem.requestFullscreen) {
  //         await elem.requestFullscreen();
  //       } else if (elem.webkitRequestFullscreen) {
  //         await elem.webkitRequestFullscreen();
  //       } else if (elem.msRequestFullscreen) {
  //         await elem.msRequestFullscreen();
  //       }
  //     } catch (err) {
  //       console.debug('Auto fullscreen request failed', err);
  //     }
  //   };

  //   window.addEventListener('touchstart', requestFullscreen, { once: true, passive: true });
  //   window.addEventListener('click', requestFullscreen, { once: true });

  //   return () => {
  //     window.removeEventListener('touchstart', requestFullscreen);
  //     window.removeEventListener('click', requestFullscreen);
  //   };
  // }, []);

  // 2. ROUTE WRAPPERS (ضعها مع باقي الـ functions قبل الـ return)
  const QuranQuiz     = () => <UniversalQuizEngine talentType="quran"     />;

  return (
    <QueryProvider key={activeAccountId || 'signed-out'}>
    <ModalProvider>
        <SnackbarProvider maxSnack={1} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <ToastProvider>
        <Router>
          <ThemeProvider>
          <LanguageProvider>
            <MaintenanceProvider><RealtimeConnection/><TasbeehSync/><OfflineTasbeehSync/><AdhkarOfflineSync/><MaintenanceGate>
            <LocalizationPreferenceSync />
            <ProfilePreviewProvider>
            <UserProvider>
              {offlineAccountId ? <React.Suspense fallback={<RouteFallback />}>
                <div className="offline-feed-shell">
                  <div className="offline-feed-actions">
                    <a href="/offline">أنشطة دون اتصال / Offline activities</a>
                    <button type="button" disabled={authLoading} onClick={() => refreshSession()}>إعادة الاتصال / Reconnect</button>
                    <button type="button" onClick={() => logout()}>تسجيل الخروج / Sign out</button>
                  </div>
                  <BackendHomePosts cachedAccountId={offlineAccountId} readOnly />
                </div>
              </React.Suspense> : <>
              <FullBanGate>
              <ApiFeatureGuard>

            <AuthenticatedAppServices />
            <PwaExitGuard />

            <AnimatedRoutes>
            <Route element={<AnimatedPageLayout />}>
          <Route path="/" element={<RequireProfile><div /></RequireProfile>} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/emergency-login" element={<EmergencyLogin />} />
          <Route path="/wallet" element={<Register />} />
          <Route path="/wallet/pay/:requestId" element={<WalletPaymentPage />} />
          <Route path="/register" element={<Navigate to="/wallet" replace />} />
          <Route path="/saraha" element={<SarahaPage />} />
          <Route path="/saraha/:userhandle" element={<SarahaSendPage />} />
          <Route path="/friends" element={<FriendsPage />} />
          <Route path="/contacts" element={<ContactsPage />} />
          <Route path="/blocked-users" element={<BlockedUsersPage />} />
          <Route path="/create" element={<BanRouteGuard banType={BAN_TYPES.PUBLISH}><CreationWorkspace><CreatePost /></CreationWorkspace></BanRouteGuard>} />
          <Route path="/create-quiz" element={<BanRouteGuard banType={BAN_TYPES.PUBLISH}><CreationWorkspace kind="quiz"><CreateQuizPage /></CreationWorkspace></BanRouteGuard>} />
          <Route path="/post" element={<RequireProfile><div /></RequireProfile>} />
          <Route path="/post/:postId" element={<BackendPostPage />} />
          <Route path="/blog/:postId" element={<BackendPostPage />} />
          <Route path="/quiz/:postId" element={<BackendPostPage />} />
          <Route path="/newspaper/:postId" element={<BackendPostPage />} />
          <Route path="/poll/:postId" element={<SinglePollPage />} />
          <Route path="/video/:postId" element={<SingleVideoPost />} />
          <Route path="/character/:category/:id" element={<CharacterDetailsPage />} />
          <Route path="/hashtag/:hashtag" element={<HashtagPage />} />
          <Route path="/team/:teamId/posts" element={<TeamPostsPage />} />
          <Route path="/frames-store" element={<StoreWorkspace><FramesStorePage /></StoreWorkspace>} />
          <Route path="/sticker-store" element={<StoreWorkspace><StickerStorePage /></StoreWorkspace>} />
          <Route path="/reaction-store" element={<StoreWorkspace><ReactionStorePage /></StoreWorkspace>} />
          <Route path="/profile-theme-store" element={<StoreWorkspace><ProfileThemeStorePage /></StoreWorkspace>} />
          <Route path="/bubble-store" element={<StoreWorkspace><BubbleStorePage /></StoreWorkspace>} />
          <Route path="/bubble-store/manage" element={<StoreWorkspace><BubbleStorePage management /></StoreWorkspace>} />
          <Route path="/create-post-list" element={<CreatePostListPage />} />
          <Route path="/list/:userId/:listId" element={<PostListPage />} />
          <Route path="/store" element={<StoreWorkspace><Store /></StoreWorkspace>} />
          <Route path="/inventory" element={<RequireProfile><InventoryPage /></RequireProfile>} />
          <Route path="/quran" element={<QuranPage />} />
          <Route path="/my-quran-list" element={<MyQuranListPage />} />
          <Route path="/characters" element={<CharactersPage />} />
          <Route path="/characters/:category/:id" element={<CharacterDetailsPage />} />
          <Route path="/edit-profile" element={<EditProfilePage />} />
          <Route path="/create-profile" element={<CreateProfilePage />} />
          <Route path='/deleted_posts' element={<PermissionRoute anyOf={['DELETED_POSTS_VIEW']}><DeletedPostsPage /></PermissionRoute>}/>
          <Route path="/manage-hashtags" element={<ManageHashtagsPage />} />
          <Route path="/administrators" element={<StaffPage />}></Route>
          <Route path="/moderation-logs" element={<PermissionRoute anyOf={['MODERATION_AUDIT_VIEW']}><ModerationLogsPage /></PermissionRoute>} />
          <Route path="/admin/deleted-accounts" element={<PermissionRoute anyOf={['ACCOUNTS_DELETION_RECOVER']}><DeletedAccountsPage /></PermissionRoute>} />
          <Route path="/admin/custom-notifications" element={<PermissionRoute anyOf={['CUSTOM_NOTIFICATIONS_SEND']}><AdminNotificationsPage /></PermissionRoute>} />
          <Route path="/admin/wallet-currencies" element={<PermissionRoute anyOf={['WALLETS_CURRENCY_RESET', 'WALLETS_CURRENCIES_VIEW', 'WALLETS_CURRENCIES_CREATE', 'WALLETS_CURRENCIES_UPDATE', 'WALLETS_CURRENCIES_TOGGLE', 'WALLETS_CURRENCIES_REORDER', 'WALLETS_CURRENCIES_DELETE']}><WalletCurrenciesPage /></PermissionRoute>} />
          <Route path="/admin/maintenance" element={<MaintenanceManager/>}/>
          <Route path="/offline" element={<div/>}/>
          <Route path="/admin/profile-media" element={<PermissionRoute anyOf={['PROFILE_MEDIA_REVIEW','PROFILE_MEDIA_POLICY_MANAGE']}><ProfileMediaReview /></PermissionRoute>} />
          <Route path="/admin/tasbeeh-competitions" element={<PermissionRoute anyOf={['TASBEEH_COMPETITIONS_MANAGE']}><CompetitionManager /></PermissionRoute>} />
          <Route path="/admin/tasbeeh-shields" element={<PermissionRoute anyOf={['TASBEEH_SHIELDS_MANAGE']}><ShieldManager /></PermissionRoute>} />
          <Route path="/admin/verification" element={<PermissionRoute anyOf={['PROFILES_VERIFY']}><AccountVerification /></PermissionRoute>} />
          <Route path="/admin/profile-badges" element={<PermissionRoute anyOf={['PROFILE_BADGES_CREATE','PROFILE_BADGES_UPDATE','PROFILE_BADGES_DELETE']}><ProfileBadgesPage /></PermissionRoute>} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/post/:post_id/comments" element={<BackendCommentThreadPage />} />
          <Route path="/post/:post_id/comment/:comment_id" element={<BackendCommentThreadPage />} />
          <Route path="/post/:post_id/comment/:comment_id/replies/:reply_id" element={<BackendCommentThreadPage />} />
          <Route path="/post/:post_id/comment/:comment_id/reply/:reply_id/single" element={<BackendCommentThreadPage />} />
          <Route path="/post/:post_id/comment/:comment_id/single" element={<BackendCommentThreadPage />} />
          <Route path="/post/:post_id/comments/:comment_id" element={<BackendCommentThreadPage />} />
          <Route path="/post/:post_id/comments/:comment_id/single" element={<BackendCommentThreadPage />} />
          <Route path="/post/:post_id/comments/:comment_id/replies/:reply_id" element={<BackendCommentThreadPage />} />
          <Route path="/post/:post_id/comments/:comment_id/replies/:reply_id/single" element={<BackendCommentThreadPage />} />
          <Route path='/more' element={<ProfileMenu/>}/>
          <Route path='/daily_quiz' element={<DailyQuiz/>}/>
          <Route path='/daily-missions' element={<DailyMissions/>}/>
          <Route path='/create-daily-quiz' element={<CreateDailyQuizPage />} />
          <Route path='/settings' element={<Settings/>}/>
          <Route path='/terms' element={<TermsOfService/>}/>
          <Route path='/privacy' element={<PrivacyPolicy/>}/>
          <Route path='/child-safety' element={<ChildSafety/>}/>
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/my-reports" element={<RequireProfile><MyReportsPage /></RequireProfile>} />
          <Route path="/messages" element={<BanRouteGuard banType={BAN_TYPES.MESSAGE}><MessagesPage /></BanRouteGuard>} />
          <Route path="/messages/chat/:id" element={<BanRouteGuard banType={BAN_TYPES.MESSAGE}><ApiChatRoomPage direct /></BanRouteGuard>} />
          <Route path="/messages/group/:id" element={<BanRouteGuard banType={BAN_TYPES.MESSAGE}><ApiChatRoomPage /></BanRouteGuard>} />
          <Route path="/messages/create-group" element={<BanRouteGuard banType={BAN_TYPES.MESSAGE}><CreateGroupPage /></BanRouteGuard>} />
          <Route path="/messages/discover" element={<DiscoverGroupsPage />} />
          <Route path="/Tasbeeh" element={<Tasbeeh />} />
          <Route path="/adhkar" element={<Adhkar />} />
          <Route path="/Tasbeeh/Counter" element={<Counter />} />
          <Route path="/Tasbeeh/Leaderboard" element={<Leaderboard />} />
          <Route path="/prayer-tracker" element={<PrayerTracker />} />
          <Route path="/saved-posts" element={<SavedPosts />} />
          <Route path="/drafts" element={<RequireProfile><DraftsPage /></RequireProfile>} />
          <Route path="/create-poll" element={<BanRouteGuard banType={BAN_TYPES.PUBLISH}><CreationWorkspace kind="poll"><CreatePoll /></CreationWorkspace></BanRouteGuard>}/>
          <Route path="/create-video" element={<LegacyCreateVideoRedirect />}/>
          <Route path="/create-plan" element={<CreatePlan />}/>
          <Route path="/talents-badges" element={<TalentsBadges />} />
          <Route path="/TalentsBadges/:categoryKey" element={<TalentLevels />} />
          <Route path="/admin/talents" element={<PermissionRoute anyOf={['TALENTS_CREATE', 'TALENT_LEVELS_UPDATE', 'TALENT_BADGE_IMAGES_UPDATE', 'TALENT_BADGE_POINTS_UPDATE']}><AdminTalentManager /></PermissionRoute>} />
          <Route path="/talents" element={<PermissionRoute anyOf={['TALENTS_CREATE', 'TALENT_LEVELS_UPDATE', 'TALENT_BADGE_IMAGES_UPDATE', 'TALENT_BADGE_POINTS_UPDATE']}><AdminTalentManager /></PermissionRoute>} />
          <Route path="/games" element={<GamesPage />} />
          <Route path="/suggestions-box" element={<PermissionRoute anyOf={['SUGGESTIONS_MANAGE']}><SuggestionsBoxPage /></PermissionRoute>} />
          <Route path="/reports" element={<PermissionRoute anyOf={['MODERATION_REPORTS_MANAGE']}><ReportsPage /></PermissionRoute>} />
          <Route path="/police-reports" element={<ProtectedRoute allowedRoles={['مسؤول قسم الشرطة', 'مركز الشرطة', 'نائب المشرف العام', 'المشرف العام', 'super_admin']}><PoliceReportsPage /></ProtectedRoute>} />
          <Route path="/literature-reports" element={<ProtectedRoute allowedRoles={['مسؤول قسم الآداب', 'مركز الآداب', 'نائب المشرف العام', 'المشرف العام', 'super_admin']}><LiteratureReportsPage /></ProtectedRoute>} />
          <Route path="/law-reports" element={<ProtectedRoute allowedRoles={['مسؤول قسم القانون', 'مركز القانون', 'نائب المشرف العام', 'المشرف العام', 'super_admin']}><LawReportsPage /></ProtectedRoute>} />
          <Route path="/health-reports" element={<ProtectedRoute allowedRoles={['مسؤول قسم الصحة', 'مركز الصحة', 'نائب المشرف العام', 'المشرف العام', 'super_admin']}><HealthReportsPage /></ProtectedRoute>} />
          <Route path="/TalentQuiz/quran"     element={<QuranQuiz />}        />
          <Route path="/islamic" element={<IslamicHub />} />
          <Route path="/events" element={<Events />} />
          <Route path="/create-event" element={<CreateEvent />} />
          <Route path="/edit-event/:eventId" element={<CreateEvent />} />
          <Route path="/adhkar-packs" element={<PacksBrowser />} />
          <Route path="/pack-editor/:packId" element={<PackEditor />} />
          <Route
            path="/profile/:user_id"
            element={
              <BackendProfile />
            }
          />
            </Route>
            </AnimatedRoutes>
              </ApiFeatureGuard>
          </FullBanGate>
              </>}
            </UserProvider>
          </ProfilePreviewProvider>
          </MaintenanceGate></MaintenanceProvider>
          </LanguageProvider>
        </ThemeProvider>
        </Router>
        </ToastProvider>
      </SnackbarProvider>
    </ModalProvider>
    </QueryProvider>
  );
}

export default App;
